import { type FC, useMemo, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import {
  type RefinementItem,
  filterLabelClasses,
  sortRefinementItems,
  useSearchableRefinementItems,
} from '@/components/sidebar/refinement-list';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { Checkbox } from '@/components/ui/checkbox';
import { EntityType } from '@/types/entity';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface TagCategoryFilterItemProps {
  // Display-only (data-cy ids) — this category has no refinementList query of its own.
  attribute: string;
  label: string;
  publicItems: Array<RefinementItem>;
  refinePublic: (value: string) => void;
}

const TagCategoryFilterItem: FC<TagCategoryFilterItemProps> = ({ attribute, label, publicItems, refinePublic }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const tCommon = useTranslations('common');
  const setPageIndex = useTableStore((s) => s.setPageIndex);

  // Only tags.public (see handleCheckboxChange) is ever refined, so it's the only one that
  // self-excludes its own filter — deriving children from it keeps sibling counts from narrowing.
  const prefix = `${label} - `;
  const items = useMemo(
    () =>
      sortRefinementItems(
        publicItems
          .filter((item) => item.value.startsWith(prefix))
          .map((item) => ({ ...item, value: item.value.slice(prefix.length), label: item.label.slice(prefix.length) })),
      ),
    [publicItems, prefix],
  );
  const canRefine = items.length > 0;

  const visibleItems = useMemo(() => (showAll ? items : items.slice(0, 10)), [items, showAll]);
  const canShowMore = items.length > 10;

  const handleCheckboxChange = (value: string) => {
    refinePublic(`${label} - ${value}`);
    setPageIndex(EntityType.Asset, 0);
  };

  if (!canRefine) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="border-b border-ui-container px-1.5 py-0.5">
      <CollapsibleTrigger asChild data-cy={`search-facet-${attribute}-second-trigger`}>
        <div className="flex w-full items-center pr-1.5">
          <div className="mr-px cursor-pointer p-1 select-none">
            <ChevronRightIcon
              className={cn('icon-5.5 text-[#999999] transition duration-300', { 'rotate-90': isExpanded })}
            />
          </div>
          <div className="flex-1">
            <button className="flex w-full items-center justify-start py-1 outline-hidden">
              <div className="flex w-full justify-between text-sm">
                <span className="line-clamp-1 text-left font-bold break-all">{label}</span>
              </div>
            </button>
          </div>
        </div>
      </CollapsibleTrigger>

      <div className={cn(isExpanded ? 'block' : 'hidden')}>
        <div className="space-y-3 py-2 pr-2 pl-4">
          {visibleItems.map((renderItem) => (
            <div key={renderItem.value} className="flex items-center space-x-2">
              <Checkbox
                data-cy={`search-facet-${attribute}-checkbox`}
                className="rounded-[3px] border-icon-base"
                id={`${label}-${renderItem.value}`}
                value={renderItem.isRefined ? 1 : 0}
                checked={renderItem.isRefined}
                onCheckedChange={() => handleCheckboxChange(renderItem.value)}
              />
              <label
                htmlFor={`${label}-${renderItem.value}`}
                className={cn(
                  'flex w-full items-center justify-between text-sm font-normal leading-none hover:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                  renderItem.isRefined ? 'text-black dark:text-white' : 'text-neutral-600 dark:text-neutral-400',
                )}
              >
                <p className={filterLabelClasses}>{renderItem.label}</p>
                {renderItem.count && <p className="ml-2 rounded-full font-light">{renderItem.count}</p>}
              </label>
            </div>
          ))}
          {canShowMore && (
            <div className="flex justify-center">
              <button
                data-cy={`search-facet-${attribute}-show-more`}
                onClick={() => setShowAll((prev) => !prev)}
                className="m-0 inline-block rounded-md px-2 py-1 text-sm font-medium text-text-base"
              >
                {showAll ? tCommon('showLess') : tCommon('showMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Collapsible>
  );
};

interface TagCategoryFacetsProps {
  // slug -> option, exactly as sent by GET /facets under the tags.facets group.
  options: Record<string, { name: string }>;
}

export const TagCategoryFacets: FC<TagCategoryFacetsProps> = ({ options }) => {
  const { items: publicItems, refine: refinePublic } = useSearchableRefinementItems('tags.public', '');

  return (
    <div className="flex flex-col">
      {Object.entries(options).map(([slug, option]) => (
        <TagCategoryFilterItem
          key={slug}
          attribute={`tags.facets.${slug}`}
          label={option.name}
          publicItems={publicItems}
          refinePublic={refinePublic}
        />
      ))}
    </div>
  );
};
